import { assertWriteIdentity, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { useCallback, useEffect, useLayoutEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Callout,
  Field,
  Icon,
  KiLabel,
  LoadingState,
  ScrollBar,
  StackList,
  Textarea,
  toastError,
} from 'admination-react'
import { useApp } from '@/core/state/appStore'
import { UnterschriftBox, unterschreibenText } from '../unterschrift/UnterschriftBox'
import { useUnterschriftOeffnen } from '../unterschrift/use-unterschrift-oeffnen'
import { useUnterschriften } from '../unterschrift/use-unterschriften'
import { KompetenzbilanzierungSperre, useKompetenzbilanzierungGespeichert } from '../kompetenzfeststellung/Kompetenzbilanzierung'
import {
  BOGEN_FEHLT,
  SprachniveauAbweichungHinweis,
  SprachniveauMarker,
  useSprachniveauStand,
} from '../sprachniveau/sprachniveau'
import { MassnahmeKopf } from '../massnahme/MassnahmeKopf'
import { AskModal } from '@/core/components/ui/ask-modal'
import { ModalHeader, type TabOverrides } from '@/core/components/modal/ModalHeader'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { useAutosave } from '@/core/hooks/useAutosave'
import { showKiMarker } from '@/core/lib/kiMarker'
import type { SprachniveauAbweichung } from '../../api/spalteStand'
import {
  ABSATZ_KEYS,
  bestaetigeErstbericht,
  bestaetigenProbleme,
  erzeugeErstbericht,
  erstberichtAblehnung,
  konfliktMeldung,
  fetchErstberichtVorschau,
  getErstberichtAbwesend,
  getErstbericht,
  speichereErstberichtEntwurf,
  textdienstNichtVerfuegbar,
  type AbsatzKey,
  type Absaetze,
  type ErstberichtBearbeitung,
  type ErstberichtAblehnung,
  type FehlendeEingabe,
  type Quellen,
} from '../../api/erstbericht'
import type { MassnahmeKopf as Massnahme } from '../../api/massnahme'

const ABSATZ_TITEL: Record<AbsatzKey, string> = {
  vorbildung: "Vorbildung",
  integrationskurs: "Integrationskurs",
  kletttest: "Klett-Test",
  sprachlicher_ausdruck: "Sprachlicher Ausdruck",
  gesamtergebnis: "Gesamtergebnis",
}

const EINGABE_TITEL: Record<string, string> = {
  kundennummer: "Kundennummer",
  lebenslauf: "Lebenslauf",
  bildung_zertifikat: "Zertifikat zum Schulabschluss",
  beruf_zertifikat: "Zertifikat zum Berufsabschluss",
  integrationskurs: "Integrationskurs",
  integrationskurs_widerspruch: "Integrationskurs",
  integrationskurs_zertifikat: "Zertifikat des Integrationskurses",
  integrationskurs_niveau: "Niveau des Integrationskurses",
  sprachlicher_ausdruck: "Angekreuztes Sprachniveau",
  sprachniveau: "Sprachniveau der Sprachlehrkraft",
  klett: "Klett-Test-Ergebnis",
}

const SPRACHNIVEAU = 'sprachniveau'

const FEHLEND_MELDUNG = "Der Erstbericht kann noch nicht erstellt werden."

type Feld = 'massnahmenummer' | AbsatzKey

type Bericht = { erzeugt: Absaetze; quellen: Quellen; texte: Absaetze }
type Operation = 'generate' | 'preview' | 'confirm'

function useAbgegebenAm(coachingId: string | null, stand: ReturnType<typeof useUnterschriften>): string | null {
  const [am, setAm] = useState<string | null>(null)
  const coachAllein = !!stand && stand !== 'laden' && stand !== 'fehler' && stand.coach && !stand.teilnehmende
  useEffect(() => {
    setAm(null)
    if (!coachingId || !coachAllein) return
    let cancelled = false
    getErstberichtAbwesend(coachingId)
      .then((gelesen) => {
        if (!cancelled) setAm(gelesen.abgegeben_am ?? null)
      })
      .catch((error: unknown) => console.error("Erstbericht abwesend laden fehlgeschlagen:", error))
    return () => {
      cancelled = true
    }
  }, [coachingId, coachAllein])
  return am
}

export function Erstbericht({ coachingId, tabs }: { coachingId: string | null; tabs?: TabOverrides }) {
  const stand = useUnterschriften(coachingId, 'erstbericht', "Erstbericht")
  const abgegebenAm = useAbgegebenAm(coachingId, stand)
  const spalte = useSprachniveauStand(coachingId, typeof stand === 'object' && stand !== null ? stand.dokumentId : null)
  const kompetenzbilanzierung = useKompetenzbilanzierungGespeichert(coachingId)
  if (stand === null) {
    if (kompetenzbilanzierung !== true) return <KompetenzbilanzierungSperre gespeichert={kompetenzbilanzierung} tabs={tabs} />
    return <ErstberichtEditor coachingId={coachingId} tabs={tabs} />
  }
  return (
    <UnterschriftBox
      dokument="erstbericht"
      coachingId={coachingId}
      tabs={tabs}
      stand={stand}
      abgegebenAm={abgegebenAm}
      marker={
        <>
          <SprachniveauAbweichungHinweis dokument="erstbericht" abweichung={spalte?.sprachniveau_abweichung} />
          <SprachniveauMarker dokument="erstbericht" stand={spalte?.sprachniveau?.erstbericht} />
        </>
      }
    />
  )
}

function ErstberichtEditor({ coachingId, tabs }: { coachingId: string | null; tabs?: TabOverrides }) {
  const { state, dispatch, reloadDashboard } = useApp()
  const unterschreibenOeffnen = useUnterschriftOeffnen('erstbericht')
  const scrollerRef = useRef<HTMLElement>(null)
  const [geladen, setGeladen] = useState(false)
  const [ladefehler, setLadefehler] = useState(false)
  const [massnahme, setMassnahme] = useState<Massnahme | null>(null)
  const [bericht, setBericht] = useState<Bericht | null>(null)
  const [bogenFehlt, setBogenFehlt] = useState(false)
  const [abweichung, setAbweichung] = useState<SprachniveauAbweichung | null>(null)
  const [action, setAction] = useState<Operation | null>(null)
  const laeuft = action === 'generate'
  const [neuFragen, setNeuFragen] = useState(false)
  const [view, setView] = useState<'form' | 'pdf'>('form')
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const pdfLoading = action === 'preview'
  const bestaetigt = action === 'confirm'
  const [ablehnung, setAblehnung] = useState<ErstberichtAblehnung | null>(null)
  const [konflikt, setKonflikt] = useState<string | null>(null)
  const [feldFehler, setFeldFehler] = useState<ReadonlyMap<Feld, string>>(new Map())
  const busy = laeuft || pdfLoading || bestaetigt
  const submitting = useRef(false)
  const operation = useRef(0)
  const alive = useRef(true)
  useLayoutEffect(() => {
    alive.current = true
    submitting.current = false
    setAction(null)
    return () => {
      alive.current = false
      operation.current += 1
    }
  }, [coachingId, state.screen?.tab])

  function current(nr: number) { return alive.current && operation.current === nr }

  useEffect(() => {
    if (!coachingId) return
    let cancelled = false
    setLadefehler(false)
    async function laden(id: string) {
      const stand = await getErstbericht(id)
      if (cancelled) return
      if (stand.fehlend.length > 0) {
        setAblehnung({ meldung: FEHLEND_MELDUNG, fehlend: stand.fehlend })
      }
      setBogenFehlt(stand.hinweise[SPRACHNIVEAU] !== undefined)
      setAbweichung(stand.sprachniveau_abweichung)
      setMassnahme(stand.kopf.massnahme)
      if (stand.entwurf) {
        const { absaetze, quellen } = stand.entwurf
        setBericht({ erzeugt: absaetze, quellen, texte: absaetze })
      }
      setGeladen(true)
    }
    laden(coachingId).catch((e: unknown) => {
      if (cancelled) return
      console.error("Erstbericht laden fehlgeschlagen:", e)
      setLadefehler(true)
      toastError("Der Erstbericht konnte nicht geladen werden.")
    })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  async function runOperation<T>(
    kind: Operation,
    request: (identity: WriteIdentity) => Promise<T>,
    accept: (result: T) => void,
    reject: (error: unknown) => void,
  ) {
    submitting.current = true
    const nr = ++operation.current
    const identity = captureWriteIdentity()
    setAction(kind)
    try {
      await flushDraft()
      assertWriteIdentity(identity)
      if (!current(nr)) return
      const result = await request(identity)
      assertWriteIdentity(identity)
      if (current(nr)) accept(result)
    } catch (error) {
      if (current(nr)) reject(error)
    } finally {
      if (current(nr)) {
        submitting.current = false
        setAction(null)
      }
    }
  }

  function erstellen() {
    if (!coachingId || konflikt || submitting.current) return
    setAblehnung(null)
    return runOperation('generate', (identity) => erzeugeErstbericht(coachingId, identity),
      (antwort) => setBericht({ erzeugt: antwort.absaetze, quellen: antwort.quellen, texte: antwort.absaetze }),
      (e) => {
        console.error("Erstbericht erstellen fehlgeschlagen:", e)
        const abgelehnt = erstberichtAblehnung(e)
        const schonBestaetigt = konfliktMeldung(e)
        if (abgelehnt) setAblehnung(abgelehnt)
        else if (schonBestaetigt) zeigeKonflikt(schonBestaetigt)
        else if (textdienstNichtVerfuegbar(e)) {
          toastError("Der Textdienst ist gerade nicht verfügbar. Bitte versuchen Sie es später erneut.")
        } else toastError("Der Erstbericht konnte nicht erstellt werden.")
      })
  }

  function zeigeKonflikt(meldung: string) {
    if (!alive.current) return
    setKonflikt(meldung)
    reloadDashboard()
  }

  function feldBearbeitet(feld: Feld) {
    setFeldFehler((prev) => {
      if (!prev.has(feld)) return prev
      const next = new Map(prev)
      next.delete(feld)
      return next
    })
  }

  function aendern(key: AbsatzKey, text: string) {
    if (submitting.current) return
    setBericht((prev) => prev && { ...prev, texte: { ...prev.texte, [key]: text } })
    feldBearbeitet(key)
  }

  const texte = bericht?.texte
  const bearbeitung = useMemo<ErstberichtBearbeitung | null>(() => (texte ? { absaetze: texte } : null), [texte])
  const gesperrt = bogenFehlt || (geladen && massnahme === null)

  const saveDraft = useCallback(
    (werte: ErstberichtBearbeitung | null): Promise<void> | undefined => {
      if (!coachingId || !werte) return undefined
      return speichereErstberichtEntwurf(coachingId, werte)
        .catch((e: unknown) => {
          console.error("Erstbericht Entwurf speichern fehlgeschlagen:", e)
          throw e
        })
    },
    [coachingId],
  )
  const { flush: flushDraft } = useAutosave(bearbeitung, bearbeitung !== null && !!coachingId && !busy, saveDraft, 250, { form: "Erstbericht", record: coachingId ?? undefined, label: "Erstbericht", recover: (value) => { if (value) setBericht((prev) => prev && { ...prev, texte: value.absaetze }) } })

  function weiter() {
    if (!coachingId || !bearbeitung || konflikt || submitting.current) return
    setPdfBlob(null)
    setView('pdf')
    return runOperation('preview', (identity) => fetchErstberichtVorschau(coachingId, bearbeitung, identity), setPdfBlob,
      (e) => {
        console.error("Erstbericht Vorschau fehlgeschlagen:", e)
        setView('form')
        const abgelehnt = erstberichtAblehnung(e)
        if (abgelehnt) setAblehnung(abgelehnt)
        else toastError("Die Vorschau konnte nicht erstellt werden.")
      })
  }

  const abweichungHinweis = <SprachniveauAbweichungHinweis dokument="erstbericht" abweichung={abweichung} />

  function bestaetigen() {
    if (!coachingId || !bearbeitung || konflikt || submitting.current) return
    return runOperation('confirm', (identity) => bestaetigeErstbericht(coachingId, bearbeitung, identity),
      () => {
        reloadDashboard()
        unterschreibenOeffnen(coachingId)
      },
      (e) => {
        console.error("Erstbericht bestätigen fehlgeschlagen:", e)
        const schonBestaetigt = konfliktMeldung(e)
        if (schonBestaetigt) {
          zeigeKonflikt(schonBestaetigt)
          return
        }
        const abgelehnt = bestaetigenProbleme(e)
        const fehlt = abgelehnt ? null : erstberichtAblehnung(e)
        if (abgelehnt) {
          toastError(abgelehnt.meldung)
          setFeldFehler(new Map(abgelehnt.probleme.map((p) => [p.feld as Feld, p.grund])))
          if (abgelehnt.probleme.some((p) => p.feld === 'massnahmenummer')) setMassnahme(null)
          setView('form')
        } else if (fehlt) {
          setAblehnung(fehlt)
          setView('form')
        } else toastError("Der Erstbericht konnte nicht bestätigt werden.")
      })
  }

  return (
    <>
      <ModalHeader size={!konflikt && view === 'pdf' ? 'document' : 'form'} tabs={tabs} onClose={() => dispatch({ type: 'CLOSE' })} closeDisabled={busy}>
        <div className="pill-cluster">

          {konflikt ? null : view === 'form' ? (
            <>
              {geladen && <div className="pill">
                <Button primaryAction={!bericht} iconOnly={!!bericht} title={bericht ? "Neu erzeugen" : undefined} disabled={busy} onClick={() => (bericht ? setNeuFragen(true) : void erstellen())}>
                  {bericht ? <Icon name="i-sparkle" /> : "Erstbericht erstellen"}
                </Button>
              </div>}
              <div className="pill">
                <Button
                  primaryAction={!!bericht}
                  iconOnly={!bericht}
                  title={!bericht ? unterschreibenText('erstbericht') : undefined}
                  disabled={!bearbeitung || busy || gesperrt}
                  onClick={() => void bestaetigen()}
                >
                  {bericht ? unterschreibenText('erstbericht') : <Icon name="i-signature" />}
                </Button>
              </div>
              <div className="pill">
                <Button iconOnly title="Weiter" disabled={!bericht || busy} onClick={() => void weiter()}>
                  <Icon name="i-doc" />
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="pill">
                <Button iconOnly title="Zurück" disabled={busy} onClick={() => setView('form')}>
                  <Icon name="i-edit" />
                </Button>
              </div>
              <div className="pill">
                <Button
                  primaryAction
                  disabled={!pdfBlob || busy || gesperrt}
                  onClick={() => void bestaetigen()}
                >
                  {unterschreibenText('erstbericht')}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalHeader>
      {konflikt ? (
        <main className="form">
          <div className="section">
            <div className="section-body">
              <Callout tone="info" role="status">
                {konflikt}
              </Callout>
            </div>
          </div>
        </main>
      ) : view === 'pdf' ? (
        <PdfRegion
          notice={abweichungHinweis}
          path={null}
          localBlob={pdfBlob}
          waiting={pdfLoading}
          loadingLabel="Die Vorschau wird erstellt …"
          placeholder="Die Vorschau konnte nicht erstellt werden."
        />
      ) : (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            <fieldset className="form-contents" disabled={busy}>
              <div className="section">
                <div className="section-body">
                  {!geladen && coachingId && (ladefehler ? (
                    <Callout tone="error" role="alert">Der Erstbericht konnte nicht geladen werden.</Callout>
                  ) : (
                    <LoadingState>Der Erstbericht wird geladen …</LoadingState>
                  ))}
                  {geladen && (
                    <>
                      <MassnahmeKopf massnahme={massnahme} testId="erstbericht-massnahme" />

                    </>
                  )}
                  {laeuft && (
                    <Callout tone="info" role="status">
                      Der Erstbericht wird erstellt …</Callout>
                  )}
                  {ablehnung && <Ablehnung ablehnung={ablehnung} />}
                  {!bericht && abweichungHinweis}
                </div>
              </div>
              {bericht && (
                <div className="section">
                  <div className="section-body">
                    {ABSATZ_KEYS.map((key) => (
                      <Absatz
                        key={key}
                        titel={ABSATZ_TITEL[key]}
                        text={bericht.texte[key]}
                        ki={showKiMarker(
                          { ki_erzeugt: bericht.quellen[key] === 'ki', origin: bericht.erzeugt[key] },
                          bericht.texte[key],
                          false,
                        )}
                        fehler={feldFehler.get(key)}
                        hinweis={key === 'sprachlicher_ausdruck' && bogenFehlt ? BOGEN_FEHLT : undefined}
                        onChange={(text) => aendern(key, text)}
                      >
                        {key === 'sprachlicher_ausdruck' && abweichungHinweis}
                      </Absatz>
                    ))}
                  </div>
                </div>
              )}
            </fieldset>
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      )}
      <AskModal
        open={neuFragen}
        busy={busy}
        icon="i-sparkle"
        iconStroke
        variant="is-warning"
        question="Erstbericht neu erzeugen?"
        description="Alle Absätze werden neu erzeugt; Ihre Änderungen an den Absätzen gehen dabei verloren."
        confirmLabel="Neu erzeugen"
        cancelLabel="Abbrechen"
        onConfirm={() => {
          setNeuFragen(false)
          void erstellen()
        }}
        onCancel={() => setNeuFragen(false)}
      />
    </>
  )
}

function Ablehnung({ ablehnung }: { ablehnung: ErstberichtAblehnung }) {
  const fehlend = ablehnung.fehlend.length > 0
  return (
    <Callout tone="error" role="alert" title={fehlend ? ablehnung.meldung : undefined}>
      {fehlend ? (
        <StackList>
          {ablehnung.fehlend.map((f, i) => (
            <li key={`${i}-${f.eingabe}`}>{eingabeZeile(f)}</li>
          ))}
        </StackList>
      ) : ablehnung.meldung}
    </Callout>
  )
}

const LEBENSLAUF_WO = "in der Übersicht der Person hochladen"

const DAMIT = "Damit der Erstbericht erstellt werden kann, muss dort"
const FEHLT = `fehlt in der Aufnahme- und Potenzialanalyse. ${DAMIT}`
const KURS_ANTWORT = `${DAMIT} „abgeschlossen“ (mit Angabe zum Zertifikat), „abgebrochen“ oder „Nein“ stehen.`

const ZEILE_ABGESCHLOSSENES: Record<string, string> = {
  bildung_zertifikat: `${FEHLT} angegeben sein, ob zum Schulabschluss ein Zertifikat vorliegt.`,
  beruf_zertifikat: `${FEHLT} angegeben sein, ob zum Berufsabschluss ein Zertifikat vorliegt.`,
  integrationskurs: `in der Aufnahme- und Potenzialanalyse gespeichert. ${KURS_ANTWORT}`,
  integrationskurs_widerspruch:
    `Der Lebenslauf nennt einen Integrationskurs, in der Aufnahme- und Potenzialanalyse fehlt aber die Angabe. ${KURS_ANTWORT}`,
  integrationskurs_zertifikat: `${FEHLT} angegeben sein, ob zum Integrationskurs ein Zertifikat vorliegt.`,
  integrationskurs_niveau: `${FEHLT} das Niveau des Integrationskurs-Zertifikats angegeben sein.`,
  sprachlicher_ausdruck: `${FEHLT} ein Sprachniveau angekreuzt sein.`,
  sprachniveau: BOGEN_FEHLT,
  klett: `im Schritt „GEVA- und Klett-Test“ nicht gespeichert. ${DAMIT} ein Klett-Test-Ergebnis stehen.`,
}

function eingabeZeile(f: FehlendeEingabe): string {
  const titel = EINGABE_TITEL[f.eingabe] ?? f.eingabe
  if (f.eingabe === 'lebenslauf') return `${titel} - ${LEBENSLAUF_WO}`
  const zeile = ZEILE_ABGESCHLOSSENES[f.eingabe]
  return zeile ? `${titel}: ${zeile}` : `${titel} - in „${f.wo}“`
}

function Absatz({
  titel,
  text,
  ki,
  fehler,
  hinweis,
  onChange,
  children,
}: {
  titel: string
  text: string
  ki: boolean
  fehler: string | undefined
  hinweis?: string
  onChange: (text: string) => void
  children?: ReactNode
}) {
  const id = useId()
  return (
    <>
      <Field label={titel} htmlFor={id} error={fehler}>
        <Textarea
          id={id}
          rows={3}
          value={text}
          aria-invalid={fehler !== undefined || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        {ki && (
          <div className="field-hint is-info field-hint--row">
            <KiLabel kind="generiert" />
          </div>
        )}
      </Field>
      {hinweis && (
        <Callout tone="warning" role="status" data-testid="erstbericht-sprachniveau-hinweis">
          {hinweis}
        </Callout>
      )}
      {children}
    </>
  )
}
